import { useCallback, useEffect, useRef, useState } from "react";
import { Filter, Plus, Search, Users, X } from "lucide-react";
import { api, candidateAPI, candidateAccessRequestAPI } from "../api/client";
import CandidateCard from "../components/CandidateCard";
import CandidateSearchCard from "../components/CandidateSearchCard";
import CandidateFormModal from "../components/modals/CandidateFormModal";
import AddFollowUpModal from "../components/modals/AddFollowUpModal";
import CallModal from "../components/CallModal";

const CRM_TAB = "crm";
const SEARCH_TAB = "search";
const CRM_PAGE_SIZE = 25;

export default function Candidates() {
  const [activeTab, setActiveTab] = useState(CRM_TAB);
  const [crmCandidates, setCrmCandidates] = useState([]);
  const [crmInitialLoading, setCrmInitialLoading] = useState(true);
  const [crmRefreshing, setCrmRefreshing] = useState(false);
  const [crmLoadingMore, setCrmLoadingMore] = useState(false);
  const [crmError, setCrmError] = useState("");
  const [crmPage, setCrmPage] = useState(1);
  const [crmTotal, setCrmTotal] = useState(0);
  const [crmHasMore, setCrmHasMore] = useState(false);
  const [facets, setFacets] = useState({ sectors: [] });
  const [facetsLoaded, setFacetsLoaded] = useState(false);
  const [query, setQuery] = useState("");
  const [sectorId, setSectorId] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState("relevance");
  const [page, setPage] = useState(1);
  const [result, setResult] = useState({ total: 0, pages: 1 });
  const [searchItems, setSearchItems] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [showCandidateForm, setShowCandidateForm] = useState(false);
  const [editingCandidate, setEditingCandidate] = useState(null);
  const [callLogCandidate, setCallLogCandidate] = useState(null);
  const [followUpCandidate, setFollowUpCandidate] = useState(null);
  const [requestCandidate, setRequestCandidate] = useState(null);
  const [requestJdId, setRequestJdId] = useState("");
  const [requestJds, setRequestJds] = useState([]);
  const [requestJdsLoading, setRequestJdsLoading] = useState(false);
  const [requestReason, setRequestReason] = useState("");
  const [requestError, setRequestError] = useState("");
  const crmHasLoaded = useRef(false);

  const loadCrm = useCallback(async ({ pageToLoad = 1, append = false } = {}) => {
    if (append) setCrmLoadingMore(true);
    else if (crmHasLoaded.current) setCrmRefreshing(true);
    else setCrmInitialLoading(true);
    setCrmError("");
    try {
      const response = await candidateAPI.getCRM({ page: pageToLoad, page_size: CRM_PAGE_SIZE });
      const nextItems = response.data.items || [];
      setCrmCandidates((currentItems) => {
        if (!append) return nextItems;
        const seen = new Set(currentItems.map((candidate) => candidate.id));
        return [...currentItems, ...nextItems.filter((candidate) => !seen.has(candidate.id))];
      });
      setCrmPage(response.data.page || pageToLoad);
      setCrmTotal(response.data.total || 0);
      setCrmHasMore(Boolean(response.data.has_more));
      crmHasLoaded.current = true;
    } catch (err) {
      setCrmError(err.response?.data?.detail || "Failed to load your CRM candidates.");
    } finally {
      setCrmInitialLoading(false);
      setCrmRefreshing(false);
      setCrmLoadingMore(false);
    }
  }, []);

  const loadSearch = useCallback(async () => {
    setSearchLoading(true);
    setSearchError("");
    try {
      const response = await candidateAPI.search({
        q: query || undefined,
        page,
        page_size: 25,
        sector_id: sectorId || undefined,
        status: status || undefined,
        sort,
      });
      setSearchItems(response.data.items || []);
      setResult(response.data);
    } catch (err) {
      setSearchError(err.response?.data?.detail || "Failed to search candidates.");
    } finally {
      setSearchLoading(false);
    }
  }, [page, query, sectorId, sort, status]);

  useEffect(() => {
    if (activeTab !== SEARCH_TAB || facetsLoaded) return;
    candidateAPI.searchFacets()
      .then((response) => setFacets(response.data))
      .catch(() => {})
      .finally(() => setFacetsLoaded(true));
  }, [activeTab, facetsLoaded]);

  useEffect(() => {
    if (activeTab === CRM_TAB && !crmHasLoaded.current) loadCrm();
  }, [activeTab, loadCrm]);

  useEffect(() => {
    if (activeTab !== SEARCH_TAB) return undefined;
    const timer = window.setTimeout(loadSearch, 300);
    return () => window.clearTimeout(timer);
  }, [activeTab, loadSearch]);

  useEffect(() => {
    setPage(1);
  }, [query, sectorId, status, sort]);

  useEffect(() => {
    if (!requestCandidate) return undefined;
    let active = true;
    setRequestJdsLoading(true);
    setRequestJdId("");
    setRequestError("");
    api.get("/jds/list", { params: { status_filter: "Active" } })
      .then((response) => {
        if (active) setRequestJds(response.data || []);
      })
      .catch((err) => {
        if (active) setRequestError(err.response?.data?.detail || "Could not load your active job descriptions.");
      })
      .finally(() => {
        if (active) setRequestJdsLoading(false);
      });
    return () => { active = false; };
  }, [requestCandidate]);

  const handleStatusChange = async (candidateId, nextStatus) => {
    await candidateAPI.update(candidateId, { status: nextStatus });
    setCrmCandidates((items) => items.map((candidate) => (
      candidate.id === candidateId ? { ...candidate, status: nextStatus } : candidate
    )));
  };

  const handleCandidateSaved = async () => {
    setShowCandidateForm(false);
    setEditingCandidate(null);
    setActiveTab(CRM_TAB);
    await loadCrm();
  };

  const handleCandidateDeleted = (candidateId) => {
    setCrmCandidates((items) => items.filter((candidate) => candidate.id !== candidateId));
    setCrmTotal((total) => Math.max(0, total - 1));
  };

  const handleCallLogSaved = (callLog) => {
    if (!callLog?.candidate_id) return;
    setCrmCandidates((items) => items.map((candidate) => (
      candidate.id === callLog.candidate_id
        ? { ...candidate, call_log_count: (candidate.call_log_count || 0) + 1 }
        : candidate
    )));
  };

  const submitRequest = async () => {
    setRequestError("");
    try {
      await candidateAccessRequestAPI.create({
        candidate_id: requestCandidate.id || Number(requestCandidate.candidate_reference.replace("CAND-", "")),
        jd_id: Number(requestJdId),
        reason: requestReason,
      });
      setRequestCandidate(null);
      setRequestJdId("");
      setRequestReason("");
    } catch (err) {
      setRequestError(err.response?.data?.detail || "Could not submit access request.");
    }
  };

  const openAccessRequest = (candidate) => {
    setRequestCandidate(candidate);
  };

  const crmCards = crmCandidates.map((candidate) => (
    <CandidateCard
      key={candidate.id}
      candidate={candidate}
      callLogCount={candidate.call_log_count || 0}
      onDelete={handleCandidateDeleted}
      onEdit={(selectedCandidate) => setEditingCandidate(selectedCandidate)}
      onResumeUpload={loadCrm}
      onCallLog={setCallLogCandidate}
      onAddFollowUp={setFollowUpCandidate}
      onStatusChange={handleStatusChange}
    />
  ));

  return <div className="space-y-6">
    <div className="flex flex-wrap items-center justify-between gap-4">
      <div>
        <h1 className="text-3xl font-heading font-bold text-gold">Candidates</h1>
        <p className="mt-1 text-textLight/70">Manage your recruitment CRM or discover protected candidates across SPES.</p>
      </div>
      {activeTab === CRM_TAB && <button onClick={() => setShowCandidateForm(true)} className="flex items-center gap-2 rounded-lg bg-teal px-4 py-2 font-medium text-white hover:bg-teal-dark"><Plus size={18} /> Add Candidate</button>}
    </div>

    <div className="flex rounded-lg border border-teal/30 bg-navy-light p-1" role="tablist" aria-label="Candidate workspace">
      <button role="tab" aria-selected={activeTab === CRM_TAB} onClick={() => setActiveTab(CRM_TAB)} className={`flex flex-1 items-center justify-center gap-2 rounded-md px-4 py-3 font-medium transition-colors ${activeTab === CRM_TAB ? "bg-teal/20 text-teal" : "text-textLight/70 hover:bg-navy hover:text-textLight"}`}><Users size={18} /> My CRM</button>
      <button role="tab" aria-selected={activeTab === SEARCH_TAB} onClick={() => setActiveTab(SEARCH_TAB)} className={`flex flex-1 items-center justify-center gap-2 rounded-md px-4 py-3 font-medium transition-colors ${activeTab === SEARCH_TAB ? "bg-teal/20 text-teal" : "text-textLight/70 hover:bg-navy hover:text-textLight"}`}><Search size={18} /> Search SPES</button>
    </div>

    {activeTab === CRM_TAB && <>
      {crmError && <div className="rounded border border-gold/50 bg-gold/10 p-3 text-gold">{crmError}</div>}
      {crmInitialLoading ? <p className="py-12 text-center text-textLight/70">Loading your CRM...</p> : crmCandidates.length === 0 ? <div className="rounded-lg border border-teal/30 bg-navy-light p-12 text-center text-textLight/70">No candidates in your CRM yet. Add a candidate or use Upload to import candidates.</div> : <>
        <div className="flex items-center justify-between text-sm text-textLight/70"><span>{crmTotal} candidate{crmTotal === 1 ? "" : "s"} in your CRM</span>{crmRefreshing && <span className="text-teal">Refreshing...</span>}</div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">{crmCards}</div>
        {crmHasMore && <div className="flex justify-center"><button onClick={() => loadCrm({ pageToLoad: crmPage + 1, append: true })} disabled={crmLoadingMore} className="rounded-lg border border-teal/40 px-5 py-2 font-medium text-teal hover:bg-teal/10 disabled:opacity-50">{crmLoadingMore ? "Loading..." : "Load more candidates"}</button></div>}
      </>}
    </>}

    {activeTab === SEARCH_TAB && <>
      <section className="rounded-lg border border-teal/30 bg-navy-light p-4 shadow-lg">
        <div className="mb-3 flex items-center gap-2 text-textLight"><Filter size={19} className="text-teal" /> Search and filters</div>
        <div className="grid gap-3 md:grid-cols-4">
          <label className="relative md:col-span-2"><Search size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-teal" /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={'Skills, role, sector; use "phrases", AND, OR, NOT'} className="w-full rounded-lg border border-teal/30 bg-navy py-2 pl-10 pr-3 text-textLight" /></label>
          <select value={sectorId} onChange={(event) => setSectorId(event.target.value)} className="rounded-lg border border-teal/30 bg-navy px-3 py-2 text-textLight"><option value="">All sectors</option>{facets.sectors.map((term) => <option key={term.id} value={term.id}>{term.name}</option>)}</select>
          <select value={status} onChange={(event) => setStatus(event.target.value)} className="rounded-lg border border-teal/30 bg-navy px-3 py-2 text-textLight"><option value="">All statuses</option><option value="Active">Active</option><option value="Inactive">Inactive</option></select>
          <select value={sort} onChange={(event) => setSort(event.target.value)} className="rounded-lg border border-teal/30 bg-navy px-3 py-2 text-textLight"><option value="relevance">Relevance</option><option value="latest_application">Latest application</option><option value="latest_added">Latest added</option><option value="experience">Experience</option></select>
        </div>
      </section>
      {searchError && <div className="rounded border border-gold/50 bg-gold/10 p-3 text-gold">{searchError}</div>}
      <p className="text-sm text-textLight/70">{searchLoading ? "Searchingâ€¦" : `${result.total || 0} candidate${result.total === 1 ? "" : "s"} found`}</p>
      {!searchLoading && searchItems.length === 0 ? <div className="rounded-lg border border-teal/30 bg-navy-light p-12 text-center text-textLight/70">No candidates match these filters.</div> : <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">{searchItems.map((candidate) => <CandidateSearchCard key={candidate.candidate_reference} candidate={candidate} onRequestAccess={openAccessRequest} />)}</div>}
      <div className="flex items-center justify-center gap-3"><button disabled={page <= 1} onClick={() => setPage(page - 1)} className="rounded border border-teal/30 px-3 py-2 text-textLight disabled:opacity-40">Previous</button><span className="text-sm text-textLight/70">Page {page} of {result.pages || 1}</span><button disabled={page >= (result.pages || 1)} onClick={() => setPage(page + 1)} className="rounded border border-teal/30 px-3 py-2 text-textLight disabled:opacity-40">Next</button></div>
    </>}

    <CandidateFormModal isOpen={showCandidateForm || Boolean(editingCandidate)} onClose={() => { setShowCandidateForm(false); setEditingCandidate(null); }} onSuccess={handleCandidateSaved} mode={editingCandidate ? "edit" : "create"} initialCandidate={editingCandidate} />
    <CallModal isOpen={Boolean(callLogCandidate)} onClose={() => setCallLogCandidate(null)} onSuccess={handleCallLogSaved} candidate={callLogCandidate} />
    <AddFollowUpModal isOpen={Boolean(followUpCandidate)} onClose={() => setFollowUpCandidate(null)} candidateId={followUpCandidate?.id} candidateName={followUpCandidate?.name} onSuccess={() => {}} />
    {requestCandidate && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"><div className="w-full max-w-md rounded-lg border border-teal/30 bg-navy-light p-5"><div className="mb-4 flex justify-between"><h2 className="font-heading text-xl text-textLight">Request candidate access</h2><button onClick={() => setRequestCandidate(null)} className="text-textLight"><X /></button></div><p className="mb-3 text-sm text-textLight/70">{requestCandidate.candidate_reference}</p>{requestJdsLoading ? <p className="mb-3 text-sm text-textLight/70">Loading your active job descriptions...</p> : requestJds.length === 0 ? <p className="mb-3 rounded border border-gold/40 bg-gold/10 p-3 text-sm text-gold">You need an active JD assigned to you or uploaded by you before requesting candidate access.</p> : <select value={requestJdId} onChange={(event) => setRequestJdId(event.target.value)} className="mb-3 w-full rounded border border-teal/30 bg-navy p-2 text-textLight"><option value="">Select an active JD</option>{requestJds.map((jd) => <option key={jd.id} value={jd.id}>JD-{String(jd.id).padStart(4, "0")} — {jd.client_name} — {jd.role_title}{jd.location ? ` — ${jd.location}` : ""}</option>)}</select>}<textarea value={requestReason} onChange={(event) => setRequestReason(event.target.value)} minLength="3" placeholder="Why is this candidate needed?" className="mb-3 min-h-24 w-full rounded border border-teal/30 bg-navy p-2 text-textLight" />{requestError && <p className="mb-3 text-sm text-gold">{requestError}</p>}<button disabled={!requestJdId || requestJdsLoading || requestJds.length === 0 || requestReason.trim().length < 3} onClick={submitRequest} className="w-full rounded bg-teal py-2 font-medium text-white disabled:opacity-40">Submit request</button></div></div>}
  </div>;
}
